import { first } from 'lodash';
import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import gql from 'graphql-tag';
import { MutationFn } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import {
  ReverifyCreditCardMutation,
  core_apimessages_CreditCard as CreditCard,
} from '../schemas/rql';
import { parseGraphqlErrors } from '@reverbdotcom/commons/src/parse_graphql_errors';
import { CSEName, useAdyenCard } from './adyen_card_hook';
import { reverbToAdyenCardType } from '../lib/adyen';
import FormGroup from '@reverbdotcom/commons/src/components/form_group';
import { RCButton } from '@reverbdotcom/cadence/components';
import { useCheckoutDisplay } from './checkout_display_context';

interface ExternalProps {
  creditCard: CreditCard;
  hasRefetch?: boolean;
  trackEvent?: () => void;
}

interface MutationProps {
  reverifyCreditCard: MutationFn<ReverifyCreditCardMutation.Mutation, ReverifyCreditCardMutation.Variables>;
}

type Props = ExternalProps & MutationProps;

const cseName: CSEName = 'encryptedSecurityCode';

export function CheckoutVerifyCardCVV(props: Props) {
  const [isLoading, setLoading] = React.useState(false);
  const checkoutDisplay = useCheckoutDisplay();
  const [submissionError, setSubmissionError] = React.useState('');
  const { focus, cardState, errors: formErrors } = useAdyenCard({
    selector: '[data-verify-cvv-field]',
    cardType: reverbToAdyenCardType(props.creditCard.cardType),
  });

  const isSubmitDisabled = checkoutDisplay.isLoading || isLoading || !cardState.isValid;
  const errorMessage = submissionError || formErrors[cseName]?.errorI18n;

  const cvvClassNames = classNames('adyen-field', {
    'adyen-field--focused': focus?.fieldType === cseName,
    'adyen-field--error': !!errorMessage,
  });

  function refetchOptions() {
    if (!props.hasRefetch) { return {}; }

    return {
      refetchQueries: ['Core_Checkout_Layout'],
      awaitRefetchQueries: true,
    };
  }

  async function submitCardCVV(event: any) {
    event.preventDefault();

    setSubmissionError('');

    setLoading(true);
    checkoutDisplay.setLoading(true);

    try {
      await reverifyCardCVV();
    } catch (errors) {
      setSubmissionError(first(parseGraphqlErrors(errors))?.message);
    }

    if (props.trackEvent) { props.trackEvent(); }

    setLoading(false);
    checkoutDisplay.setLoading(false);
  }

  async function reverifyCardCVV() {
    const tokenizedFieldsJson = JSON.stringify(cardState.data.paymentMethod);

    const result = await props.reverifyCreditCard({
      variables: {
        input: {
          creditCardId: props.creditCard.id,
          tokenizedFields: tokenizedFieldsJson,
        },
      },
      ...refetchOptions(),
    });

    if (!result) { return {}; }

    return result.data?.reverifyCreditCard?.creditCard;
  }

  return (
    <FormGroup
      label={I18n.t('discovery.creditCardForm.pleaseReverifyCvv')}
      inputName="cardCVV"
      helpText={I18n.t('discovery.creditCardForm.cvvLocation')}
      errorText={errorMessage}
    >
      <div className="d-flex fx-align-start">
        <div data-verify-cvv-field className="width-rem-10 mr-1">
          <div className={cvvClassNames} data-cse={cseName} />
        </div>
        <RCButton
          variant="primary"
          onClick={submitCardCVV}
          disabled={isSubmitDisabled}
        >
          {I18n.t('discovery.creditCardForm.verifyCvvButton')}
        </RCButton>
      </div>
    </FormGroup>
  );
}

export const withMutation = withGraphql<ExternalProps, ReverifyCreditCardMutation.Mutation, ReverifyCreditCardMutation.Variables>(
  gql`mutation reverifyCreditCardMutation($input: Input_core_apimessages_ReverifyCreditCardRequest) {
    reverifyCreditCard(input: $input) {
      creditCard {
        id
        last4
        cardType
        expirationYear
        expirationMonth
        cardholderName
        needsReverification
        primaryForCheckout
      }
    }
  }`,
  {
    name: 'reverifyCreditCard',
  });

export default withMutation(CheckoutVerifyCardCVV);
